/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import useLocalStorageState from 'use-local-storage-state';

import { useFirebaseAnalytics } from '~/apis/useFirebase';
import {
  isMergeCardsState,
  selectConnectionTimeout,
  selectCurrentHorseIndex,
  selectCurrentState,
  selectHorseCardDeck,
  selectIsCurrentOwnHorse,
  selectIsInteractiveGameplayState,
  selectOwnHorse,
  selectPlayerId,
  selectRound,
  selectTurnType,
  useSelector,
} from '~/app';
import { CardValue, sendAddBonusCards } from '~/overlay';
import HorseSelection, { HorseSelectionProps } from '~/overlay/horse-selection/HorseSelection';
import TurnControls, { TurnControlsProps } from '~/overlay/turn-controls/TurnControls';
import Turfmaster from '~/server/src/tmmodel';
import { useKeyPress } from '~/utils';
import { cn } from '~/utils/styles';

import Card from './Card';

// Define constant default object to prevent new reference creation
const DEFAULT_CARD_DECK = { cards: undefined, bonuscards: undefined };

interface BarBottomProps extends TurnControlsProps, HorseSelectionProps {
  selectedOwnHorseIndex: number;
  selectedCardLocalIndex: number;
  /** When true, show turn controls (dice/move); derived in Overlay from phase and acting player. */
  shouldShowTurnControls?: boolean;
  /** When true, we are in SELECTDICES phase (show dice area to all players regardless of turn). */
  isDiceSelection?: boolean;
  /** When false, horse selection clicks are ignored (e.g. during dice phases). */
  canSwitchViewedHorse?: boolean;
  onCardSelected: (index: number) => void;
  /** When true, cards are shown as backs only (spectators must not see values). */
  isSpectatorMode?: boolean;
  /** When true, interactions are disabled (viewing in-game history). */
  isHistoryMode?: boolean;
  /** When true, history panel is open (arrow keys should be disabled to prevent conflicts). */
  isHistoryPanelOpen?: boolean;
  /** When true, user just submitted a move (bonus card disabled until we leave SELECTDICES or round changes). */
  moveJustSubmitted?: boolean;
  /** When true, user just submitted bonus cards (disabled until state/round update). */
  bonusJustSubmitted?: boolean;
  /** Called when user dispatches add bonus cards (to set bonusJustSubmitted in parent). */
  onBonusCardsSubmitted?: () => void;
}

const BarBottom = ({
  selectedOwnHorseIndex,
  selectedCardLocalIndex,
  shouldShowTurnControls = true,
  isDiceSelection = false,
  canSwitchViewedHorse = true,
  onCardSelected,
  onCardMoveSelected,
  isDiceShuffling,
  onOwnHorseSelected,
  isSpectatorMode = false,
  isHistoryMode = false,
  isHistoryPanelOpen = false,
  moveJustSubmitted = false,
  bonusJustSubmitted = false,
  onBonusCardsSubmitted,
}: BarBottomProps): JSX.Element => {
  const analytics = useFirebaseAnalytics();

  const connectionTimeout = useSelector(selectConnectionTimeout);
  const [isConnectionTimeoutEnabled] = useLocalStorageState('isConnectionTimeoutEnabled', {
    defaultValue: true,
  });

  const isCurrentOwnHorse = useSelector(selectIsCurrentOwnHorse);
  const round = useSelector(selectRound);
  const currentState = useSelector(selectCurrentState);
  const isInteractiveGameplayState = useSelector(selectIsInteractiveGameplayState);
  const turnType = useSelector(selectTurnType);
  const playerId = useSelector(selectPlayerId);
  const currentHorseIndex = useSelector(selectCurrentHorseIndex);
  const horse = useSelector((state) =>
    selectedOwnHorseIndex >= 0 ? selectOwnHorse(state, selectedOwnHorseIndex) : undefined,
  );
  const { cards, bonuscards } = useSelector((state) =>
    horse ? selectHorseCardDeck(state, horse.index) : DEFAULT_CARD_DECK,
  );
  const horseColor = horse ? horse.color : 0;

  // Only show selection highlight on the deck that belongs to the current horse (avoids same index appearing selected on the other horse's deck).
  const effectiveSelectedIndex = horse?.index === currentHorseIndex ? selectedCardLocalIndex : -1;

  const currentCards =
    horse && cards && cards.length >= round + 1 && cards[round] ? cards[round] : [];

  const currentBonusCards =
    horse &&
    bonuscards === -1 &&
    cards &&
    cards.length === Turfmaster.rounds + 1 &&
    cards[Turfmaster.rounds]
      ? cards[Turfmaster.rounds]
      : [];

  const cardsCount = currentCards
    ? currentCards.length + (currentBonusCards.length > 0 ? 1 : 0)
    : 0;
  const offsetToCenterDeck = ((cardsCount - 1) * 50) / 2;
  const offsetAroundSelected = (index: number) => {
    if (effectiveSelectedIndex > 0) {
      if (index < effectiveSelectedIndex) return -20;
      else if (index > effectiveSelectedIndex) return 20;
    }
    return 0;
  };

  const dispatch = useDispatch();
  const onBonusCardClicked = () => {
    if (!horse || bonusCardDisabled || !canAddBonusCardsNow) return;
    dispatch(sendAddBonusCards({ playerid: playerId, horseid: horse.index }));
    onBonusCardsSubmitted?.();
    onCardSelected(-1);

    analytics?.logEvent('use_bonuscards', { round, turnType, isCurrentOwnHorse });
  };

  // if game is in merge mode than mergable cards are kept in the previous cardStack so
  // we can detect if a horse has not merged yet by checking if the horse has multiple cardStacks
  const areSelectedOwnHorseCardsUnmerged = useMemo(() => {
    const cardsCount =
      cards?.reduce((prevCounter, curCards) => {
        if (curCards.length > 0) return prevCounter + 1;
        return prevCounter;
      }, 0) || 0;
    return cardsCount > 1;
  }, [cards]);

  const showConnectionTimeoutHint = isConnectionTimeoutEnabled && connectionTimeout;

  // Server only accepts ADDBONUSCARDS when currentState is in stateMatrix and array includes ADDBONUSCARDS
  const canAddBonusCardsNow =
    (Turfmaster.stateMatrix as Record<number, number[] | undefined>)[currentState]?.includes(
      Turfmaster.stateMachine.ADDBONUSCARDS,
    ) ?? false;

  const cardsInvisible = areSelectedOwnHorseCardsUnmerged && isMergeCardsState(currentState);

  const cardsDisabled =
    showConnectionTimeoutHint ||
    isHistoryMode ||
    (round === 0 && !isInteractiveGameplayState) ||
    turnType === Turfmaster.turntypes.DICES ||
    currentHorseIndex !== horse?.index ||
    isMergeCardsState(currentState);

  // Bonus card: same as cardsDisabled but NOT turnType DICES or round 0 (in DICES we are allowed to take bonus cards). Also disabled after user just submitted a move.
  const bonusCardsDisabled =
    showConnectionTimeoutHint ||
    isHistoryMode ||
    currentHorseIndex !== horse?.index ||
    isMergeCardsState(currentState);
  const bonusCardDisabled =
    bonusCardsDisabled || moveJustSubmitted || bonusJustSubmitted || !canAddBonusCardsNow;

  // arrow left: select left card (only when viewing current horse's deck)
  // Don't handle if history panel is open to prevent conflicts with history navigation
  const onLeftArrow = useCallback(() => {
    if (isHistoryPanelOpen) return; // History panel handles arrow keys when open
    if (horse?.index !== currentHorseIndex) return; // Only change selection when viewing current horse's deck
    if (selectedCardLocalIndex > 0) onCardSelected(selectedCardLocalIndex - 1);
    else if (selectedCardLocalIndex < 0) onCardSelected(Math.floor(currentCards.length / 2));
  }, [
    currentCards.length,
    currentHorseIndex,
    horse?.index,
    onCardSelected,
    selectedCardLocalIndex,
    isHistoryPanelOpen,
  ]);
  useKeyPress(37, onLeftArrow);

  // arrow right: select right card (only when viewing current horse's deck)
  // Don't handle if history panel is open to prevent conflicts with history navigation
  const onRightArrow = useCallback(() => {
    if (isHistoryPanelOpen) return; // History panel handles arrow keys when open
    if (horse?.index !== currentHorseIndex) return; // Only change selection when viewing current horse's deck
    if (selectedCardLocalIndex >= 0) {
      if (selectedCardLocalIndex < currentCards.length - 1)
        onCardSelected(selectedCardLocalIndex + 1);
      else onCardSelected(currentCards.length - 1);
    } else onCardSelected(Math.floor(currentCards.length / 2));
  }, [
    currentCards.length,
    currentHorseIndex,
    horse?.index,
    onCardSelected,
    selectedCardLocalIndex,
    isHistoryPanelOpen,
  ]);
  useKeyPress(39, onRightArrow);

  const calculatedTranslateY = useMemo(() => {
    const maxTranslateY = 60;
    const minTranslateY = 40;
    const translateY = Math.min(
      maxTranslateY,
      minTranslateY + cardsCount * Math.round((maxTranslateY - minTranslateY) / 12),
    );
    return `${translateY}%`;
  }, [cardsCount]);

  return (
    <div
      className={cn(
        'pointer-events-none absolute right-0 bottom-0 left-0 h-[15%] w-screen rounded-t-lg border-x border-t border-black/50 bg-black/25 px-4',
        'flex flex-col',
      )}
    >
      <div className="grid grow grid-cols-[20%_50%_30%]">
        <div className="pointer-events-auto flex h-full flex-col">
          <HorseSelection
            selectedOwnHorse={selectedOwnHorseIndex}
            onOwnHorseSelected={onOwnHorseSelected}
            isHistoryMode={isHistoryMode}
            canSwitchViewedHorse={canSwitchViewedHorse}
          />
        </div>

        <div className="relative h-full">
          <div
            className={cn(
              'absolute top-0 right-0 bottom-0 left-0 flex flex-row items-end justify-center',
              'h-[15%]',

              {
                'opacity-0': cardsInvisible,
              },
            )}
            style={{ transform: `translateY(${calculatedTranslateY}) scale(1.5)` }}
          >
            {currentCards.length > 0 &&
              currentCards.map((card, index) => {
                const offsetCard = -50 * index;
                return (
                  <Card
                    key={card + index}
                    value={card as CardValue}
                    isFaceDown={isSpectatorMode}
                    className={cn(
                      'max-w-[10vh]',
                      'pointer-events-auto cursor-pointer self-start transition-all',
                      {
                        'cursor-not-allowed brightness-50': cardsDisabled,
                      },
                    )}
                    color={horseColor}
                    onClick={() => onCardSelected(index === effectiveSelectedIndex ? -1 : index)}
                    style={{
                      transform: `translate3d(${
                        offsetCard + offsetToCenterDeck + offsetAroundSelected(index)
                      }%, ${effectiveSelectedIndex === index ? -10 : 0}%, 0)`, // popup selected card
                    }}
                  />
                );
              })}

            {currentBonusCards.length > 0 && (
              <Card
                key={'bonus'}
                value={0}
                isFaceDown={false}
                className={cn('max-w-[10vh]', 'w-auto self-start transition-all', {
                  'pointer-events-auto cursor-pointer': !bonusCardDisabled,
                  'pointer-events-none cursor-not-allowed brightness-50': bonusCardDisabled,
                  'opacity-50': showConnectionTimeoutHint,
                })}
                color={horseColor}
                onClick={onBonusCardClicked}
                style={{
                  transform: `translate3d(${
                    -50 * (cardsCount - 1) +
                    offsetToCenterDeck +
                    offsetAroundSelected(cardsCount - 1)
                  }%, 0, 0)`,
                }}
              />
            )}
          </div>
        </div>

        <TurnControls
          onCardMoveSelected={onCardMoveSelected}
          isDiceShuffling={isDiceShuffling}
          isDiceSelection={isDiceSelection}
          shouldShowTurnControls={shouldShowTurnControls}
        />
      </div>

      {/* Overlay / Backdrop component to block rest of UI behind popover */}
      {/* REMOVED: Connection timeout overlay, now handled in FloatingMenu */}

      {/* (Temporary) reload button that suggest the user to reload the page if no message was received for a while */}
      {/* Removed: Popover with reload button */}
    </div>
  );
};

export default BarBottom;
